/**
 * The three questions this site actually asks, as three functions.
 *
 * The plugin's option bag could express a hundred dialogs, and the code used it
 * to build the same four over and over, each spelled slightly differently. This
 * is what those four look like when they are named instead of configured:
 *
 *   ui.alert   here is something you should know
 *   ui.confirm are you sure, yes or no
 *   ui.prompt  type something, or do not
 *
 * They answer with what the caller actually wants, a boolean or a string or
 * nothing, rather than a result object to pick apart. New code should use
 * these; ui/swal.js stays for the calls that have not moved yet.
 */
const { openDialog } = require('./dialog');
const { FIELD, LABEL, el, chip } = require('./style');

/** @returns {Node|null} */
function textBody(text) {
    return text ? el('p', '', text) : null;
}

/**
 * Something to read and acknowledge.
 *
 * @param {Object} options - { title, text, tone, closeLabel }
 * @returns {Promise<void>}
 */
function alert(options) {
    const config = options || {};
    return openDialog({
        title: config.title,
        icon: chip(config.tone),
        body: textBody(config.text),
        buttons: [{ id: 'confirm', label: config.closeLabel || 'OK', tone: 'primary', autofocus: true }]
    }).result.then(function () { });
}

/**
 * Yes or no.
 *
 * @param {Object} options - { title, text, tone, confirmLabel, cancelLabel, danger }
 * @returns {Promise<boolean>}
 */
function confirm(options) {
    const config = options || {};
    return openDialog({
        title: config.title,
        icon: chip(config.tone || (config.danger ? 'warning' : 'question')),
        body: textBody(config.text),
        buttons: [
            { id: 'cancel', label: config.cancelLabel || 'Cancel', tone: 'neutral' },
            {
                id: 'confirm',
                label: config.confirmLabel || 'Confirm',
                tone: config.danger ? 'danger' : 'primary',
                autofocus: !config.danger // a destructive default should be reached for, not landed on
            }
        ]
    }).result.then(outcome => outcome.action === 'confirm');
}

/**
 * Ask for one value.
 *
 * `validate` runs on the way out and may return a message to show instead of
 * closing, or a promise of one, which is how a prompt that has to check
 * something with the server stays open while it does.
 *
 * @param {Object} options - { title, text, label, placeholder, value, multiline,
 *   tone, confirmLabel, cancelLabel, validate }
 * @returns {Promise<string|null>} null when it was dismissed
 */
function prompt(options) {
    const config = options || {};

    const body = document.createDocumentFragment();
    if (config.text) body.appendChild(el('p', 'mb-3', config.text));

    if (config.label) body.appendChild(el('label', LABEL, config.label));

    const field = el(config.multiline ? 'textarea' : 'input', FIELD);
    if (config.multiline) field.rows = 4;
    if (config.placeholder) field.placeholder = config.placeholder;
    if (config.value) field.value = String(config.value);
    body.appendChild(field);

    const controller = openDialog({
        title: config.title,
        icon: chip(config.tone),
        body,
        focus: field,
        buttons: [
            { id: 'cancel', label: config.cancelLabel || 'Cancel', tone: 'neutral' },
            { id: 'confirm', label: config.confirmLabel || 'Save', tone: 'primary' }
        ],
        onAction: function (id, ctl) {
            if (id !== 'confirm') return undefined;
            if (typeof config.validate !== 'function') return field.value;

            ctl.setLoading(true);
            return Promise.resolve(config.validate(field.value)).then(function (message) {
                ctl.setLoading(false);
                if (!message) return field.value;
                ctl.setValidation(String(message));
                return false;
            });
        }
    });

    if (!config.multiline) {
        field.addEventListener('keydown', function (event) {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            const button = controller.button('confirm');
            if (button && !button.disabled) button.click();
        });
    }

    return controller.result.then(outcome => (outcome.action === 'confirm' ? outcome.value : null));
}

module.exports = { alert, confirm, prompt };
